import React, { useState } from "react";
import { IEvent, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { apiService } from "src/services/api.service";

import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import StopWatch, { convertTimeToSeconds, convertTimeToMinutes } from "src/components/EnterResults/StopWatch/StopWatch";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./AdditionalSettingsTab.module.scss";

interface IProps {
  event: IEvent,
  show: IShow,
}

const AdditionalSettingsTab: React.FC<IProps> = ({ event, show }: IProps) => {
  // Check if this is a UKC Agility show
  const isUkcAgility = event.sanctioning_club === Constants.sanctioning_club.UKC && 
                       show.show_type === Constants.show_type.agility;

  // For UKC Agility, get all agility levels from show_elements
  const agilityLevels = isUkcAgility
    ? (show.show_elements || [])
        .filter(se => se.show_element === Constants.dog_class_element.agility && se.level)
        .map(se => se.level!)
    : [];

  // State for per-level ACT values (stored as MM:SS:TT strings)
  const [actByLevel, setActByLevel] = useState<{ level: string; act: string }[]>(() => {
    if (isUkcAgility && agilityLevels.length > 0) {
      // Initialize from show.act_by_level or use show.act as default for all levels
      return agilityLevels.map(level => {
        const actSeconds = show.act_by_level?.find(config => config.level === level)?.act || show.act || 0;
        return {
          level,
          act: actSeconds > 0 ? convertTimeToMinutes(actSeconds) : "00:00:00"
        };
      });
    }
    return [];
  });

  // Legacy single ACT for non-UKC-Agility or backward compatibility
  const [act, setACT] = useState<string>(show.act?.toString() ?? "");

  const handleACTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setACT(e.currentTarget.value);
  }

  const handleLevelACTChange = (level: string, value: string) => {
    setActByLevel(prev => prev.map(item => 
      item.level === level ? { ...item, act: value } : item
    ));
  }

  const handleSubmit = () => {
    const updateData: any = { ...show };
    
    if (isUkcAgility && actByLevel.length > 0) {
      // Convert MM:SS:TT format to decimal seconds and save as act_by_level array
      updateData.act_by_level = actByLevel
        .filter(item => item.act && item.act.trim() !== "" && item.act !== "00:00:00")
        .map(item => ({ 
          level: item.level, 
          act: convertTimeToSeconds(item.act) 
        }));
    } else {
      // Save legacy single act value
      updateData.act = Number(act);
    }

    apiService.updateShow(event._id, show._id, updateData)
      .then(() => {
        toast.show({
          title: "Update Additional Settings",
          content: "Additional Settings has been successfully updated",
          duration: 10000,
          type: "success"
        });
      })
      .catch(error => {
        toast.show({
          title: "Update Additional Settings",
          content: "Failed to update Additional Settings",
          duration: 10000,
          type: "fail"
        });
      });
  }

  return (
    <div className={styles.AdditionalSettingsTab}>
      {isUkcAgility && actByLevel.length > 0 ? (
        <>
          <h3>Standard Course Time (SCT) by Level</h3>
          <p>Configure SCT for each agility level in MM:SS format. This will be used to calculate the ACT for each division.</p>
          <div className={styles.actInputsGrid}>
            {actByLevel.map(item => (
              <div key={item.level} className={styles.actInputGroup}>
                <div className={styles.levelLabel}>{item.level}</div>
                <StopWatch
                  label="SCT"
                  time={item.act}
                  onChange={(value) => handleLevelACTChange(item.level, value)}
                  hideTT={true}
                />
              </div>
            ))}
          </div>
        </>
      ) : (
        <Input 
          label="Assigned Course Time (ACT)" 
          type="number" 
          defaultValue={act} 
          onChange={handleACTChange} 
          placeholder="Enter an ACT" 
        />
      )}
      <div className={styles.actions}>
        <Button isLoading={false} onClick={handleSubmit}>Save</Button>
      </div>
    </div>
  );
};

export default AdditionalSettingsTab;
